//frontend/dispatcher-client/src/components/EditInvoice.js
import React, { useState, useRef, useMemo, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import axios from "../axiosConfig";
import { customSelectStyles } from "../styles/reactSelectStyles.js";
import { FaRegCalendarAlt, FaRegClock } from "react-icons/fa";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import "../styles/EditInvoice.css";
import { useTranslation } from "react-i18next";

export default function EditInvoice() {
  const { t } = useTranslation("edit_invoice");
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const location = useLocation();

  const issueDateRef = useRef(null);
  const MAX_POINTS = 7;

  const emptyPoint = () => ({
    location: "",
    date: "",
    hour: "",
  });

  const [pickupPoints, setPickupPoints] = useState([emptyPoint()]);
  const [deliveryPoints, setDeliveryPoints] = useState([emptyPoint()]);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [clientOption, setClientOption] = useState(null);
  const [status, setStatus] = useState(null);
  const [price, setPrice] = useState("");
  const [issueDate, setIssueDate] = useState("");
  const [expiryOption, setExpiryOption] = useState(null);

  const [clientOptions, setClientOptions] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);

  const [errors, setErrors] = useState({});
  const [loadingError, setLoadingError] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);

  const toYYYYMMDD = (value) => {
    if (!value) return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";
    return d.toISOString().slice(0, 10);
  };

  const clampToKnownDays = (n) => {
    if (!Number.isFinite(n) || n <= 0) return null;
    const known = [30, 45, 60, 90];
    if (known.includes(n)) return n;

    let best = known[0];
    let bestDiff = Math.abs(n - known[0]);

    for (let i = 1; i < known.length; i++) {
      const diff = Math.abs(n - known[i]);
      if (diff < bestDiff) {
        best = known[i];
        bestDiff = diff;
      }
    }
    return best;
  };

  const daysDiff = (fromYmd, toYmd) => {
    if (!fromYmd || !toYmd) return null;
    const a = new Date(fromYmd);
    const b = new Date(toYmd);
    if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return null;
    const ms = b - a;
    const d = Math.round(ms / (1000 * 60 * 60 * 24));
    return Number.isFinite(d) ? d : null;
  };

  const normalizeHourInput = (value) => {
    const digits = String(value || "").replace(/\D/g, "").slice(0, 4);
    if (!digits) return "";
    if (digits.length <= 2) return digits;
    return `${digits.slice(0, 2)}:${digits.slice(2)}`;
  };

  const clearError = (key) => {
    setErrors((prev) => {
      if (!prev[key]) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const statusOptions = useMemo(
    () => [
      { value: "DRAFT", label: t("status.DRAFT", { defaultValue: "Draft" }) },
      { value: "SENT", label: t("status.SENT", { defaultValue: "Sent" }) },
      { value: "PARTIAL", label: t("status.PARTIAL", { defaultValue: "Partial" }) },
      { value: "PAID", label: t("status.PAID", { defaultValue: "Paid" }) },
      { value: "VOID", label: t("status.VOID", { defaultValue: "Void" }) },
    ],
    [t]
  );

  const expiryOptions = useMemo(
    () => [30, 45, 60, 90].map((d) => ({ value: d, label: t("expiry.days", { count: d }) })),
    [t]
  );

  const clientSelectStyles = useMemo(() => {
    return {
      ...customSelectStyles,
      option: (base, state) => ({
        ...base,
        backgroundColor: state.isFocused ? "#232937" : "#1b1f26",
        color: "#e8f0ff",
        paddingTop: 10,
        paddingBottom: 10,
        cursor: "pointer",
      }),
      singleValue: (base) => ({
        ...base,
        color: "#e8f0ff",
      }),
    };
  }, []);

  const formatClientOptionLabel = (option, { context }) => {
    if (context === "value") {
      return <span>{option.label}</span>;
    }

    return (
      <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
        <span style={{ fontWeight: 600 }}>{option.label}</span>
        {option.clientTypeLabel ? (
          <span style={{ fontSize: "11px", opacity: 0.75, marginTop: 2 }}>
            {option.clientTypeLabel}
          </span>
        ) : null}
      </div>
    );
  };

  const handleCreateClientOption = (inputValue) => {
    const trimmed = String(inputValue || "").trim();
    if (!trimmed) return;

    const newOption = {
      value: trimmed,
      label: trimmed,
      clientType: null,
      clientTypeLabel: null,
      __isNew__: true,
    };

    setClientOption(newOption);
    clearError("clientName");
  };

  useEffect(() => {
    let cancelled = false;

    const fetchClients = async () => {
      try {
        setIsLoadingClients(true);

        const [industriesRes, carriersRes] = await Promise.all([
          axios.get("/industries"),
          axios.get("/carriers"),
        ]);

        const industries = Array.isArray(industriesRes?.data) ? industriesRes.data : [];
        const carriers = Array.isArray(carriersRes?.data) ? carriersRes.data : [];

        const industryOptions = industries
          .map((item) => {
            const name =
              item?.name ||
              item?.industryName ||
              item?.companyName ||
              item?.clientName ||
              "";

            if (!String(name).trim()) return null;

            return {
              value: String(name).trim(),
              label: String(name).trim(),
              clientType: "industry",
              clientTypeLabel: t("client_type.Industry", { defaultValue: "Industry" }),
            };
          })
          .filter(Boolean);

        const carrierOptions = carriers
          .map((item) => {
            const name =
              item?.name ||
              item?.carrierName ||
              item?.companyName ||
              item?.clientName ||
              "";

            if (!String(name).trim()) return null;

            return {
              value: String(name).trim(),
              label: String(name).trim(),
              clientType: "carrier",
              clientTypeLabel: t("client_type.Carrier", { defaultValue: "Carrier" }),
            };
          })
          .filter(Boolean);

        const merged = [...industryOptions, ...carrierOptions];

        const uniqueMap = new Map();
        for (const opt of merged) {
          const key = `${opt.label.toLowerCase()}__${opt.clientType}`;
          if (!uniqueMap.has(key)) uniqueMap.set(key, opt);
        }

        const sorted = Array.from(uniqueMap.values()).sort((a, b) =>
          a.label.localeCompare(b.label, undefined, { sensitivity: "base" })
        );

        if (!cancelled) setClientOptions(sorted);
      } catch (err) {
        console.error("❌ Failed to fetch invoice clients:", err);
        if (!cancelled) setClientOptions([]);
      } finally {
        if (!cancelled) setIsLoadingClients(false);
      }
    };

    fetchClients();

    return () => {
      cancelled = true;
    };
  }, [t]);

  const updatePoint = (type, index, field, value) => {
    const setter = type === "pickup" ? setPickupPoints : setDeliveryPoints;

    setter((prev) =>
      prev.map((point, i) => (i === index ? { ...point, [field]: value } : point))
    );
  };

  const addPoint = (type) => {
    const setter = type === "pickup" ? setPickupPoints : setDeliveryPoints;

    setter((prev) => {
      if (prev.length >= MAX_POINTS) return prev;
      return [...prev, emptyPoint()];
    });
  };

  const removePoint = (type, index) => {
    const setter = type === "pickup" ? setPickupPoints : setDeliveryPoints;

    setter((prev) => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== index);
    });

    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${type}Location-${index}`];
      delete next[`${type}Date-${index}`];
      delete next[`${type}Hour-${index}`];
      return next;
    });
  };

  const buildPointsFromInvoice = (raw, type) => {
    const listKey = type === "pickup" ? "pickupList" : "deliveryList";
    const locationKey = type === "pickup" ? "pickup" : "delivery";
    const locationAltKey = type === "pickup" ? "pickupLocation" : "deliveryLocation";
    const dateKey = type === "pickup" ? "pickupDate" : "deliveryDate";
    const hourKey = type === "pickup" ? "pickupHour" : "deliveryHour";

    if (Array.isArray(raw?.[listKey]) && raw[listKey].length > 0) {
      return raw[listKey].map((point) => ({
        location: String(point?.location || point?.place || "").trim(),
        date: toYYYYMMDD(point?.date),
        hour: String(point?.hour || "").trim(),
      }));
    }

    return [
      {
        location: String(raw?.[locationKey] || raw?.[locationAltKey] || "").trim(),
        date: toYYYYMMDD(raw?.[dateKey]),
        hour: String(raw?.[hourKey] || "").trim(),
      },
    ];
  };

  useEffect(() => {
    if (!invoiceId || invoiceId === "undefined") {
      setLoadingError(t("messages.error_load", { defaultValue: "Failed to load invoice." }));
      return;
    }

    let ignore = false;

    const fillFromRaw = (raw) => {
      const nextPickupPoints = buildPointsFromInvoice(raw, "pickup");
      const nextDeliveryPoints = buildPointsFromInvoice(raw, "delivery");

      setInvoiceNumber(String(raw?.invoiceNumber ?? raw?.invoiceId ?? raw?.id ?? ""));
      setPickupPoints(nextPickupPoints.length ? nextPickupPoints : [emptyPoint()]);
      setDeliveryPoints(nextDeliveryPoints.length ? nextDeliveryPoints : [emptyPoint()]);
      setPrice(raw?.price != null ? String(raw.price) : "");
      setIssueDate(toYYYYMMDD(raw?.issueDate || raw?.date));

      const statusValue = typeof raw?.status === "object" ? raw?.status?.value : raw?.status;
      setStatus(
        statusOptions.find((opt) => opt.value === String(statusValue || "DRAFT").toUpperCase()) || null
      );

      let days = null;

      if (raw?.net_payment_days_carrier != null) {
        const n = parseInt(raw.net_payment_days_carrier, 10);
        if (Number.isInteger(n)) days = n;
      }

      if (days == null) {
        const diff = daysDiff(
          toYYYYMMDD(raw?.issueDate || raw?.date),
          toYYYYMMDD(raw?.paymentDueDate)
        );
        if (Number.isInteger(diff) && diff > 0) days = diff;
      }

      if (days == null && typeof raw?.expiryDays === "number") {
        days = raw.expiryDays;
      }

      const clampedDays = clampToKnownDays(days);
      setExpiryOption(
        clampedDays != null ? expiryOptions.find((o) => o.value === clampedDays) || null : null
      );

      const rawClient = String(raw?.clientName || raw?.client || "").trim();
      const rawClientType =
        typeof raw?.clientType === "object" ? raw?.clientType?.value ?? null : raw?.clientType ?? null;

      if (rawClient) {
        const normalizedType = rawClientType ? String(rawClientType).trim().toLowerCase() : null;

        const existing =
          clientOptions.find(
            (opt) =>
              opt.label.toLowerCase() === rawClient.toLowerCase() &&
              (normalizedType ? opt.clientType === normalizedType : true)
          ) ||
          clientOptions.find((opt) => opt.label.toLowerCase() === rawClient.toLowerCase());

        setClientOption(
          existing
            ? {
                ...existing,
                industryClientId:
                  existing.industryClientId ??
                  (normalizedType === "industry" ? raw?.industryClientId ?? null : null),
                carrierClientId:
                  existing.carrierClientId ??
                  (normalizedType === "carrier" ? raw?.carrierClientId ?? null : null),
              }
            : {
                value: rawClient,
                label: rawClient,
                clientType: normalizedType || null,
                clientTypeLabel: normalizedType
                  ? normalizedType === "industry"
                    ? t("client_type.Industry", { defaultValue: "Industry" })
                    : t("client_type.Carrier", { defaultValue: "Carrier" })
                  : null,
                industryClientId:
                  normalizedType === "industry" ? raw?.industryClientId ?? null : null,
                carrierClientId:
                  normalizedType === "carrier" ? raw?.carrierClientId ?? null : null,
                __isNew__: true,
              }
        );
      } else {
        setClientOption(null);
      }
    };

    const fetchInvoice = async () => {
      try {
        setLoadingError("");

        const prefill = location.state?.prefill;
        if (prefill && !ignore) fillFromRaw(prefill);

        const { data } = await axios.get(`/invoices/${invoiceId}`);
        if (ignore) return;

        fillFromRaw(data);
      } catch (err) {
        console.error("❌ Failed to load invoice:", err);
        if (!ignore) {
          setLoadingError(t("messages.error_load", { defaultValue: "Failed to load invoice." }));
        }
      }
    };

    fetchInvoice();

    return () => {
      ignore = true;
    };
  }, [invoiceId, location.state, t, statusOptions, expiryOptions, clientOptions]);

  const reqLabel = (label, plural = false) =>
    plural
      ? t("validation.required_plural", { field: label, defaultValue: `${label} are required.` })
      : t("validation.required_singular", { field: label, defaultValue: `${label} is required.` });

  const validate = () => {
    const next = {};

    if (!clientOption?.label?.trim()) next.clientName = reqLabel(t("fields.client_name", { defaultValue: "Client Name" }));
    if (!status?.value) next.status = reqLabel(t("fields.status", { defaultValue: "Status" }));

    pickupPoints.forEach((point, index) => {
      if (!point.location?.trim()) {
        next[`pickupLocation-${index}`] = reqLabel(t("fields.pickup_place", { defaultValue: "Pick-up Place" }));
      }
      if (!point.date) {
        next[`pickupDate-${index}`] = reqLabel(t("fields.pickup_date", { defaultValue: "Pick-up Date" }));
      }
      if (!point.hour?.trim()) {
        next[`pickupHour-${index}`] = reqLabel(t("fields.pickup_hour", { defaultValue: "Pick-up Hour" }));
      }
    });

    deliveryPoints.forEach((point, index) => {
      if (!point.location?.trim()) {
        next[`deliveryLocation-${index}`] = reqLabel(t("fields.delivery_place", { defaultValue: "Delivery Place" }));
      }
      if (!point.date) {
        next[`deliveryDate-${index}`] = reqLabel(t("fields.delivery_date", { defaultValue: "Delivery Date" }));
      }
      if (!point.hour?.trim()) {
        next[`deliveryHour-${index}`] = reqLabel(t("fields.delivery_hour", { defaultValue: "Delivery Hour" }));
      }
    });

    const pNorm =
      typeof price === "string" ? price.replace(",", ".").trim() : String(price ?? "").trim();

    if (!pNorm) next.price = reqLabel(t("fields.price", { defaultValue: "Price" }));
    else if (!Number.isFinite(Number(pNorm))) {
      next.price = t("validation.invalid_number", {
        field: t("fields.price", { defaultValue: "Price" }),
        defaultValue: "Invalid number.",
      });
    }

    if (!issueDate) next.issueDate = reqLabel(t("fields.issue_date", { defaultValue: "Issue Date" }));
    if (!expiryOption?.value) {
      next.expiryOption = reqLabel(
        t("fields.payment_expiration", { defaultValue: "Payment Expiration" }),
        true
      );
    }

    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    try {
      const expiryDays =
        typeof expiryOption?.value === "number"
          ? expiryOption.value
          : parseInt(expiryOption.value, 10);

      const d = new Date(issueDate);
      d.setDate(d.getDate() + expiryDays);
      const paymentDueDate = d.toISOString().slice(0, 10);

      const priceNormalized =
        typeof price === "string" ? price.replace(",", ".").trim() : String(price).trim();

      const cleanedPickupPoints = pickupPoints.map((point) => ({
        location: point.location.trim(),
        date: point.date,
        hour: point.hour.trim(),
      }));

      const cleanedDeliveryPoints = deliveryPoints.map((point) => ({
        location: point.location.trim(),
        date: point.date,
        hour: point.hour.trim(),
      }));

      const payload = {
        client: clientOption.label.trim(),
        clientType: clientOption?.clientType ?? null,
        industryClientId:
          clientOption?.clientType === "industry"
            ? clientOption?.industryClientId ?? null
            : null,
        carrierClientId:
          clientOption?.clientType === "carrier"
            ? clientOption?.carrierClientId ?? null
            : null,
        issueDate,
        expiryDays,
        paymentDueDate,

        hour: null,
        transportsessionId: null,
        loadId: null,

        pickup: cleanedPickupPoints[0]?.location || null,
        pickupDate: cleanedPickupPoints[0]?.date || null,
        pickupHour: cleanedPickupPoints[0]?.hour || null,

        delivery: cleanedDeliveryPoints[cleanedDeliveryPoints.length - 1]?.location || null,
        deliveryDate: cleanedDeliveryPoints[cleanedDeliveryPoints.length - 1]?.date || null,
        deliveryHour: cleanedDeliveryPoints[cleanedDeliveryPoints.length - 1]?.hour || null,

        pickupCount: cleanedPickupPoints.length,
        deliveryCount: cleanedDeliveryPoints.length,
        pickupList: cleanedPickupPoints,
        deliveryList: cleanedDeliveryPoints,

        pickupLocation: cleanedPickupPoints[0]?.location || null,
        deliveryLocation: cleanedDeliveryPoints[cleanedDeliveryPoints.length - 1]?.location || null,

        price: String(priceNormalized),
        status: status.value,
      };

      await axios.put(`/invoices/${invoiceId}`, payload);

      setErrorMessage("");
      setShowSuccessPopup(true);

      setTimeout(() => {
        navigate("/invoices", { replace: true });
      }, 1200);
    } catch (err) {
      console.error("❌ Failed to update invoice:", err);
      setShowSuccessPopup(false);
      setErrorMessage(
        err?.response?.data?.error ||
          t("messages.error_update", {
            defaultValue: "Falha ao atualizar o documento de cobrança.",
          })
      );
    }
  };

  return (
    <div className="edit-invoice-container">
      <div className="edit-invoice-back-arrow" onClick={() => navigate("/invoices")}>
        ← {t("page.back")}
      </div>

      <div className="edit-invoice-header">
        <h2 className="edit-invoice-title">{t("page.title")}</h2>
      </div>

      {errorMessage && (
        <p className="edit-invoice-error-message">{errorMessage}</p>
      )}

      {showSuccessPopup && (
        <div className="edit-invoice-successPopup-overlay">
          <div className="edit-invoice-successPopup">
            <div className="edit-invoice-successPopup-icon">✓</div>

            <h3>
              {t("success.updatedTitle", {
                defaultValue: "Documento atualizado",
              })}
            </h3>

            <p>
              {t("success.updated", {
                defaultValue: "Documento de cobrança atualizado com sucesso.",
              })}
            </p>

            <button
              type="button"
              onClick={() => navigate("/invoices", { replace: true })}
              className="edit-invoice-successPopup-btn"
            >
              {t("actions.ok", {
                defaultValue: "OK",
              })}
            </button>
          </div>
        </div>
      )}

      <div className="edit-invoice-form">
        {loadingError ? (
          <div className="edit-invoice-error" style={{ marginTop: 0, marginBottom: 12 }}>
            {loadingError}
          </div>
        ) : null}

        <div className="edit-invoice-form-top">
          <div className="edit-invoice-id">
            <span className="edit-invoice-id-label">{t("fields.invoice_id")}</span>
            <span className="edit-invoice-id-value">{invoiceNumber}</span>
          </div>
        </div>

        <div className="edit-invoice-form-row edit-invoice-form-row--two">
          <div className="edit-invoice-field-wrap">
            <label className="edit-invoice-label">{t("fields.client_name")}*</label>
            <div className={`edit-invoice-select ${errors.clientName ? "has-error" : ""}`}>
              <CreatableSelect
                classNamePrefix="edit-invoice-select"
                options={clientOptions}
                value={clientOption}
                onChange={(opt) => {
                  setClientOption(opt);
                  if (opt?.label?.trim()) clearError("clientName");
                }}
                onCreateOption={handleCreateClientOption}
                placeholder={t("placeholders.client_name")}
                styles={clientSelectStyles}
                formatOptionLabel={formatClientOptionLabel}
                isClearable
                isLoading={isLoadingClients}
              />
            </div>
            {errors.clientName && (
              <div className="edit-invoice-error edit-invoice-error--select">
                {errors.clientName}
              </div>
            )}
          </div>

          <div className="edit-invoice-field-wrap">
            <label className="edit-invoice-label">{t("fields.status")}*</label>
            <div className={`edit-invoice-select ${errors.status ? "has-error" : ""}`}>
              <Select
                classNamePrefix="edit-invoice-select"
                options={statusOptions}
                value={status}
                onChange={(opt) => {
                  setStatus(opt);
                  if (opt?.value) clearError("status");
                }}
                placeholder={t("placeholders.status")}
                styles={customSelectStyles}
                isClearable
              />
            </div>
            {errors.status && (
              <div className="edit-invoice-error edit-invoice-error--select">
                {errors.status}
              </div>
            )}
          </div>
        </div>

        <div className="edit-invoice-form-row edit-invoice-form-row--two">
          <div className="edit-invoice-field-wrap">
            <label className="edit-invoice-label">{t("fields.pickup_place")}*</label>

            {pickupPoints.map((point, index) => (
              <div key={`pickup-${index}`} className="edit-invoice-point-card">
                <input
                  className={`edit-invoice-input ${errors[`pickupLocation-${index}`] ? "input-error" : ""}`}
                  type="text"
                  value={point.location}
                  onChange={(e) => {
                    const v = e.target.value;
                    updatePoint("pickup", index, "location", v);
                    if (v?.trim()) clearError(`pickupLocation-${index}`);
                  }}
                  placeholder={t("placeholders.pickup_place")}
                />
                {errors[`pickupLocation-${index}`] && (
                  <div className="edit-invoice-error">{errors[`pickupLocation-${index}`]}</div>
                )}

                <div className="edit-invoice-inline-datetime">
                  <div className="edit-invoice-inline-date">
                    <div className="edit-invoice-input-with-icon edit-invoice-input-with-icon--compact">
                      <input
                        className={`edit-invoice-input ${errors[`pickupDate-${index}`] ? "input-error" : ""}`}
                        type="date"
                        value={point.date}
                        onPointerDown={(e) => {
                          try {
                            e.currentTarget.showPicker?.();
                          } catch (_) {}
                        }}
                        onChange={(e) => {
                          const v = e.target.value;
                          updatePoint("pickup", index, "date", v);
                          if (v) clearError(`pickupDate-${index}`);
                        }}
                      />
                      <FaRegCalendarAlt className="edit-invoice-calendar-icon" />
                    </div>
                    {errors[`pickupDate-${index}`] && (
                      <div className="edit-invoice-error">{errors[`pickupDate-${index}`]}</div>
                    )}
                  </div>

                  <div className="edit-invoice-inline-time">
                    <div className="edit-invoice-input-with-icon edit-invoice-input-with-icon--compact">
                      <input
                        className={`edit-invoice-input ${errors[`pickupHour-${index}`] ? "input-error" : ""}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        value={point.hour}
                        onChange={(e) => {
                          const v = normalizeHourInput(e.target.value);
                          updatePoint("pickup", index, "hour", v);
                          if (v?.trim()) clearError(`pickupHour-${index}`);
                        }}
                        placeholder="HH:MM"
                      />
                      <FaRegClock className="edit-invoice-calendar-icon" />
                    </div>
                    {errors[`pickupHour-${index}`] && (
                      <div className="edit-invoice-error">{errors[`pickupHour-${index}`]}</div>
                    )}
                  </div>
                </div>

                {pickupPoints.length > 1 && (
                  <button
                    type="button"
                    className="edit-invoice-remove-point-btn"
                    onClick={() => removePoint("pickup", index)}
                  >
                    {t("buttons.remove") || "Remove"}
                  </button>
                )}
              </div>
            ))}

            {pickupPoints.length < MAX_POINTS && (
              <button
                type="button"
                className="edit-invoice-add-point-btn"
                onClick={() => addPoint("pickup")}
              >
                + {t("buttons.add_another_pickup") || "Adicionar outra Recolha"}
              </button>
            )}
          </div>

          <div className="edit-invoice-field-wrap">
            <label className="edit-invoice-label">{t("fields.delivery_place")}*</label>

            {deliveryPoints.map((point, index) => (
              <div key={`delivery-${index}`} className="edit-invoice-point-card">
                <input
                  className={`edit-invoice-input ${errors[`deliveryLocation-${index}`] ? "input-error" : ""}`}
                  type="text"
                  value={point.location}
                  onChange={(e) => {
                    const v = e.target.value;
                    updatePoint("delivery", index, "location", v);
                    if (v?.trim()) clearError(`deliveryLocation-${index}`);
                  }}
                  placeholder={t("placeholders.delivery_place")}
                />
                {errors[`deliveryLocation-${index}`] && (
                  <div className="edit-invoice-error">{errors[`deliveryLocation-${index}`]}</div>
                )}

                <div className="edit-invoice-inline-datetime">
                  <div className="edit-invoice-inline-date">
                    <div className="edit-invoice-input-with-icon edit-invoice-input-with-icon--compact">
                      <input
                        className={`edit-invoice-input ${errors[`deliveryDate-${index}`] ? "input-error" : ""}`}
                        type="date"
                        value={point.date}
                        onPointerDown={(e) => {
                          try {
                            e.currentTarget.showPicker?.();
                          } catch (_) {}
                        }}
                        onChange={(e) => {
                          const v = e.target.value;
                          updatePoint("delivery", index, "date", v);
                          if (v) clearError(`deliveryDate-${index}`);
                        }}
                      />
                      <FaRegCalendarAlt className="edit-invoice-calendar-icon" />
                    </div>
                    {errors[`deliveryDate-${index}`] && (
                      <div className="edit-invoice-error">{errors[`deliveryDate-${index}`]}</div>
                    )}
                  </div>

                  <div className="edit-invoice-inline-time">
                    <div className="edit-invoice-input-with-icon edit-invoice-input-with-icon--compact">
                      <input
                        className={`edit-invoice-input ${errors[`deliveryHour-${index}`] ? "input-error" : ""}`}
                        type="text"
                        inputMode="numeric"
                        maxLength={5}
                        value={point.hour}
                        onChange={(e) => {
                          const v = normalizeHourInput(e.target.value);
                          updatePoint("delivery", index, "hour", v);
                          if (v?.trim()) clearError(`deliveryHour-${index}`);
                        }}
                        placeholder="HH:MM"
                      />
                      <FaRegClock className="edit-invoice-calendar-icon" />
                    </div>
                    {errors[`deliveryHour-${index}`] && (
                      <div className="edit-invoice-error">{errors[`deliveryHour-${index}`]}</div>
                    )}
                  </div>
                </div>

                {deliveryPoints.length > 1 && (
                  <button
                    type="button"
                    className="edit-invoice-remove-point-btn"
                    onClick={() => removePoint("delivery", index)}
                  >
                    {t("buttons.remove") || "Remove"}
                  </button>
                )}
              </div>
            ))}

            {deliveryPoints.length < MAX_POINTS && (
              <button
                type="button"
                className="edit-invoice-add-point-btn"
                onClick={() => addPoint("delivery")}
              >
                + {t("buttons.add_another_delivery") || "Adicionar outra Descarga"}
              </button>
            )}
          </div>
        </div>

        <div className="edit-invoice-form-row">
          <div className="edit-invoice-field-wrap">
            <label className="edit-invoice-label">{t("fields.price")}*</label>
            <input
              className={`edit-invoice-input ${errors.price ? "input-error" : ""}`}
              type="number"
              value={price}
              onChange={(e) => {
                const v = e.target.value;
                setPrice(v);
                if (v?.trim()) clearError("price");
              }}
              placeholder={t("placeholders.price")}
              inputMode="decimal"
            />
            {errors.price && <div className="edit-invoice-error">{errors.price}</div>}
          </div>

          <div className="edit-invoice-field-wrap">
            <label className="edit-invoice-label">{t("fields.issue_date")}*</label>
            <div className="edit-invoice-input-with-icon">
              <input
                ref={issueDateRef}
                className={`edit-invoice-input ${errors.issueDate ? "input-error" : ""}`}
                type="date"
                value={issueDate}
                onPointerDown={(e) => {
                  try {
                    e.currentTarget.showPicker?.();
                  } catch (_) {}
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  setIssueDate(v);
                  if (v) clearError("issueDate");
                }}
              />
              <FaRegCalendarAlt
                className="edit-invoice-calendar-icon"
                onPointerDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  try {
                    issueDateRef.current?.showPicker?.();
                  } catch (_) {
                    issueDateRef.current?.focus?.();
                  }
                }}
                title={t("tooltips.open_calendar")}
              />
            </div>
            {errors.issueDate && <div className="edit-invoice-error">{errors.issueDate}</div>}
          </div>

          <div className="edit-invoice-field-wrap">
            <label className="edit-invoice-label">{t("fields.payment_expiration")}*</label>
            <div className={`edit-invoice-select ${errors.expiryOption ? "has-error" : ""}`}>
              <Select
                classNamePrefix="edit-invoice-select"
                options={expiryOptions}
                value={expiryOption}
                onChange={(opt) => {
                  setExpiryOption(opt);
                  if (opt?.value) clearError("expiryOption");
                }}
                placeholder={t("placeholders.payment_expiration")}
                styles={customSelectStyles}
                isClearable
              />
            </div>
            {errors.expiryOption && (
              <div className="edit-invoice-error edit-invoice-error--select">
                {errors.expiryOption}
              </div>
            )}
          </div>
        </div>

        <div className="edit-invoice-buttons-row">
          <button
            className="edit-invoice-btn edit-invoice-cancel-btn"
            onClick={() => navigate("/invoices")}
          >
            {t("buttons.cancel")}
          </button>
          <button
            className="edit-invoice-btn edit-invoice-submit-btn"
            onClick={handleSave}
          >
            {t("buttons.save_changes")}
          </button>
        </div>
      </div>
    </div>
  );
}